import { Outlet, createRootRouteWithContext, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import type { AuthContextValue } from './auth/auth-provider';
import { RequireRole } from './auth/require-role';
import { AppShell } from './layouts/app-shell';
import { PortalLayout } from './components/layout/portal-layout';
import { RootLayout } from './layouts/root-layout';

/**
 * Route tree — manual composition (no codegen yet) so the skeleton has zero
 * build-time prerequisites beyond what's already in package.json.
 *
 * Structure:
 *   /                         landing rozcestník (auth: HomePage, anon: redirect /login)
 *
 *   Public chrome (no AppShell):
 *     /login
 *     /accept-invite
 *     /forgot-password
 *     /reset-password
 *
 *   Authenticated chrome (AppShell with sidebar + topbar):
 *     /                                — dlaždicový rozcestník (#72025)
 *     /dashboard
 *     /orders                          (admin, decision_maker, partner) — unified list
 *     /orders/$id                      (admin, decision_maker) — order detail
 *     /offers                          (admin, decision_maker) — unified offers list
 *     /offers/$id                      (admin, decision_maker) — offer detail
 *     /partners                        (admin, decision_maker) — Adresář (sjednoceno z CRM, #72193)
 *     /crm                             (admin, decision_maker)
 *     /crm/customers                   (admin, decision_maker) — redirect → /partners (#72193 konsolidace)
 *     /crm/aktivity                    (admin, decision_maker) — CRM Aktivity (M5 placeholder)
 *     /vyroba/poptavky                 (admin, decision_maker) — RFQ k dodavatelům (placeholder)
 *     /vyroba/poptavky/$source/$id     (admin, decision_maker) — detail poptávky (source: app | obis)
 *     /reports                         (admin, decision_maker) — rozcestník reportů
 *     /reports/sales-pipeline          (admin, decision_maker) — skeleton, PR #90
 *     /reports/customers               (admin, decision_maker) — skeleton, PR #90
 *     /reports/operations              (admin, decision_maker) — skeleton, PR #90
 *     /reports/order-flow              (admin, decision_maker) — skeleton, PR #90
 *     /admin/users (+ /new, /:id)      (admin)
 *     /admin/sync                      (admin) — sync história (re-uses SyncHistoryCard)
 *     /admin/outbox                    (admin) — outbox status (re-uses OutboxStatusCard)
 *     /admin/cron                      (admin) — registered cron jobs / interval tasks
 *     /admin/logs                      (admin) — audit log (placeholder, M3 dokončení)
 *     /admin/codebooks                 (admin) — číselníky (status mapping přehled)
 *     /admin/docs                      (admin) — uživatelská dokumentace
 *     /admin/docs/data                 (admin) — dokumentace dat (placeholder, PR #91)
 *     /admin/changelog                 (admin) — changelog (CHANGELOG.md, #67626)
 *     /profile/change-password         (any authenticated user)
 *
 * Role allow-lists mirror `components/nav/nav-items.ts` — when you add a
 * new section update both the nav data and the route guard here.
 */

export interface RouterContext {
  auth: AuthContextValue;
  queryClient: QueryClient;
}

const RouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/router-devtools').then((m) => ({ default: m.TanStackRouterDevtools })),
    )
  : null;

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function Root() {
    return (
      <RootLayout>
        <Outlet />
        {RouterDevtools ? (
          <Suspense fallback={null}>
            <RouterDevtools position="bottom-right" />
          </Suspense>
        ) : null}
      </RootLayout>
    );
  },
  notFoundComponent: function NotFound() {
    return (
      <div className="flex min-h-full items-center justify-center p-8">
        <div className="card max-w-md text-center">
          <h1 className="text-xl font-semibold">Stránka nenalezena</h1>
          <p className="mt-2 text-sm text-slate-600">
            Požadovaná adresa neexistuje nebo k ní nemáte přístup.
          </p>
        </div>
      </div>
    );
  },
});

// Lazy imports keep the route module graph small in the initial chunk.
const LoginPage = lazy(() => import('./pages/login-page').then((m) => ({ default: m.LoginPage })));
const HomePage = lazy(() => import('./pages/home-page').then((m) => ({ default: m.HomePage })));
const DashboardPage = lazy(() =>
  import('./pages/dashboard-page').then((m) => ({ default: m.DashboardPage })),
);
const ChangePasswordPage = lazy(() =>
  import('./pages/change-password-page').then((m) => ({ default: m.ChangePasswordPage })),
);
const UsersListPage = lazy(() =>
  import('./pages/admin/users-list-page').then((m) => ({ default: m.UsersListPage })),
);
const UserCreatePage = lazy(() =>
  import('./pages/admin/user-create-page').then((m) => ({ default: m.UserCreatePage })),
);
const UserEditPage = lazy(() =>
  import('./pages/admin/user-edit-page').then((m) => ({ default: m.UserEditPage })),
);
const RolesListPage = lazy(() =>
  import('./pages/admin/roles-list-page').then((m) => ({ default: m.RolesListPage })),
);
const RoleCreatePage = lazy(() =>
  import('./pages/admin/role-create-page').then((m) => ({ default: m.RoleCreatePage })),
);
const RoleEditPage = lazy(() =>
  import('./pages/admin/role-edit-page').then((m) => ({ default: m.RoleEditPage })),
);
const DocsPage = lazy(() =>
  import('./pages/admin/docs-page').then((m) => ({ default: m.DocsPage })),
);
const AcceptInvitePage = lazy(() =>
  import('./pages/accept-invite-page').then((m) => ({ default: m.AcceptInvitePage })),
);
const ForgotPasswordPage = lazy(() =>
  import('./pages/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('./pages/reset-password-page').then((m) => ({ default: m.ResetPasswordPage })),
);

const OrdersPage = lazy(() =>
  import('./pages/sections/orders-page').then((m) => ({ default: m.OrdersPage })),
);
const OrderDetailPage = lazy(() =>
  import('./pages/sections/order-detail-page').then((m) => ({ default: m.OrderDetailPage })),
);
const OffersPage = lazy(() =>
  import('./pages/sections/offers-page').then((m) => ({ default: m.OffersPage })),
);
const OfferDetailPage = lazy(() =>
  import('./pages/sections/offer-detail-page').then((m) => ({ default: m.OfferDetailPage })),
);
const PartnersPage = lazy(() =>
  import('./pages/sections/partners-page').then((m) => ({ default: m.PartnersPage })),
);
// Config je eager (lehký modul) — PartnersPage je code-split, ale potřebuje
// config při renderu (#72154 — jedna komponenta, dva režimy).
import { ADDRESS_BOOK_CONFIG, PARTNERS_CONFIG } from './pages/sections/partners-view-config';
const PartnerDetailPage = lazy(() =>
  import('./pages/sections/partner-detail-page').then((m) => ({ default: m.PartnerDetailPage })),
);
const PartnerOverviewPage = lazy(() =>
  import('./pages/partner-overview-page').then((m) => ({ default: m.PartnerOverviewPage })),
);
// Partner portal (#72154) — separate /portal/* shell + pages.
const PortalHomePage = lazy(() =>
  import('./pages/portal/portal-home-page').then((m) => ({ default: m.PortalHomePage })),
);
const PortalProfilePage = lazy(() =>
  import('./pages/portal/portal-profile-page').then((m) => ({ default: m.PortalProfilePage })),
);
const PortalMachinesPage = lazy(() =>
  import('./pages/portal/portal-machines-page').then((m) => ({ default: m.PortalMachinesPage })),
);
const PortalOrdersPage = lazy(() =>
  import('./pages/portal/portal-orders-page').then((m) => ({ default: m.PortalOrdersPage })),
);
const PortalOrderDetailPage = lazy(() =>
  import('./pages/portal/portal-order-detail-page').then((m) => ({
    default: m.PortalOrderDetailPage,
  })),
);
const PortalCalendarPage = lazy(() =>
  import('./pages/portal/portal-calendar-page').then((m) => ({ default: m.PortalCalendarPage })),
);
const CrmPage = lazy(() =>
  import('./pages/sections/crm-page').then((m) => ({ default: m.CrmPage })),
);
const CrmActivitiesPage = lazy(() =>
  import('./pages/sections/crm-activities-page').then((m) => ({ default: m.CrmActivitiesPage })),
);
const MeetingsPage = lazy(() =>
  import('./pages/sections/meetings-page').then((m) => ({ default: m.MeetingsPage })),
);
const PipelinePage = lazy(() =>
  import('./pages/sections/pipeline-page').then((m) => ({ default: m.PipelinePage })),
);
const ManufacturingRfqPage = lazy(() =>
  import('./pages/sections/manufacturing-rfq-page').then((m) => ({
    default: m.ManufacturingRfqPage,
  })),
);
const RfqDetailPage = lazy(() =>
  import('./pages/sections/rfq-detail-page').then((m) => ({ default: m.RfqDetailPage })),
);
const ManufacturingDashboardPage = lazy(() =>
  import('./pages/manufacturing-dashboard-page').then((m) => ({
    default: m.ManufacturingDashboardPage,
  })),
);
const CrmDashboardPage = lazy(() =>
  import('./pages/crm-dashboard-page').then((m) => ({ default: m.CrmDashboardPage })),
);
const ReportsOverviewPage = lazy(() =>
  import('./pages/sections/reports-overview-page').then((m) => ({
    default: m.ReportsOverviewPage,
  })),
);
const ReportSalesPipelinePage = lazy(() =>
  import('./pages/sections/report-sales-pipeline-page').then((m) => ({
    default: m.ReportSalesPipelinePage,
  })),
);
const ReportCustomersPage = lazy(() =>
  import('./pages/sections/report-customers-page').then((m) => ({
    default: m.ReportCustomersPage,
  })),
);
const ReportCustomerOrdersPage = lazy(() =>
  import('./pages/sections/report-customer-orders-page').then((m) => ({
    default: m.ReportCustomerOrdersPage,
  })),
);
const ReportPartnerOrdersPage = lazy(() =>
  import('./pages/sections/report-partner-orders-page').then((m) => ({
    default: m.ReportPartnerOrdersPage,
  })),
);
const ReportOperationsPage = lazy(() =>
  import('./pages/sections/report-operations-page').then((m) => ({
    default: m.ReportOperationsPage,
  })),
);
const ReportOrderFlowPage = lazy(() =>
  import('./pages/sections/report-order-flow-page').then((m) => ({
    default: m.ReportOrderFlowPage,
  })),
);
const SyncPage = lazy(() =>
  import('./pages/admin/sync-page').then((m) => ({ default: m.SyncPage })),
);
const OutboxPage = lazy(() =>
  import('./pages/admin/outbox-page').then((m) => ({ default: m.OutboxPage })),
);
const CronJobsPage = lazy(() =>
  import('./pages/admin/cron-jobs-page').then((m) => ({ default: m.CronJobsPage })),
);
const LogsPage = lazy(() =>
  import('./pages/admin/logs-page').then((m) => ({ default: m.LogsPage })),
);
const CodebooksPage = lazy(() =>
  import('./pages/admin/codebooks-page').then((m) => ({ default: m.CodebooksPage })),
);
const DataDocsPage = lazy(() =>
  import('./pages/admin/data-docs-page').then((m) => ({ default: m.DataDocsPage })),
);
const ChangelogPage = lazy(() =>
  import('./pages/admin/changelog-page').then((m) => ({ default: m.ChangelogPage })),
);

import { createRoute } from '@tanstack/react-router';

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  staticData: { breadcrumb: undefined },
  beforeLoad: async ({ context, location }) => {
    // Wait for the silent-refresh bootstrap to settle before deciding —
    // otherwise the first load may pass through with `status='bootstrapping'`
    // and TanStack Router won't re-run `beforeLoad` when the status flips
    // to `anonymous` (#72120). The promise resolves immediately in tests
    // that mock auth context as already settled.
    await context.auth.whenBootstrapped();
    const auth = context.auth.getSnapshot();
    // Anonymní uživatel jde na login. Auth uživatel zůstává na `/`
    // a vidí HomePage (dlaždicový rozcestník, #72025). Partner má vlastní
    // portál (#72154) — přesměruj ho tam místo interního rozcestníku.
    if (auth.status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.pathname } });
    }
    if (auth.user?.role === 'partner') {
      throw redirect({ to: '/portal' });
    }
  },
  component: () => protectedShell(<HomePage />),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: async ({ context }) => {
    // Mirror the index-route wait: if a refresh succeeds we must bounce the
    // user away from the login form to the home rozcestník.
    await context.auth.whenBootstrapped();
    const auth = context.auth.getSnapshot();
    if (auth.status === 'authenticated') {
      // Partner lands in the portal; everyone else in the internal home.
      throw redirect({ to: auth.user?.role === 'partner' ? '/portal' : '/' });
    }
  },
  component: function LoginRoute() {
    return (
      <Suspense fallback={<RouteFallback />}>
        <LoginPage />
      </Suspense>
    );
  },
});

async function authGuard(context: RouterContext, pathname: string) {
  // Same rationale as `indexRoute.beforeLoad` — wait for the bootstrap to
  // settle so the guard can apply a deterministic auth check. Without the
  // await a protected route would render briefly under `status='bootstrapping'`
  // (guard returns silently) and never re-evaluate when the refresh resolves
  // as `anonymous`.
  await context.auth.whenBootstrapped();
  if (context.auth.getSnapshot().status !== 'authenticated') {
    throw redirect({ to: '/login', search: { redirect: pathname } });
  }
}

/**
 * Wrap a route's component output in the AppShell. All authenticated
 * section pages flow through this so the sidebar/topbar stay consistent.
 */
function protectedShell(node: React.ReactNode) {
  return (
    <AppShell>
      <Suspense fallback={<RouteFallback />}>{node}</Suspense>
    </AppShell>
  );
}

/**
 * Wrap a portal page in the partner PortalLayout + a partner-only role guard.
 * The backend scope filter is the real authorization; RequireRole keeps a
 * non-partner from rendering the portal chrome.
 */
function portalShell(node: React.ReactNode) {
  return (
    <PortalLayout>
      <Suspense fallback={<RouteFallback />}>
        <RequireRole allow={['partner']}>{node}</RequireRole>
      </Suspense>
    </PortalLayout>
  );
}

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  staticData: { breadcrumb: 'Přehled' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => protectedShell(<DashboardPage />),
});

const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile/change-password',
  staticData: { breadcrumb: 'Změna hesla' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => protectedShell(<ChangePasswordPage />),
});

// ─── Section routes (placeholder pages until M2+) ───────────────────────────

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/orders',
  staticData: { breadcrumb: 'Zakázky' },
  // URL search params drive the list filters so the operator can share a
  // deep link ("Filipe, koukni na tohle"). Validation is permissive — any
  // unknown value falls back to undefined and renders the unfiltered list.
  validateSearch: (search: Record<string, unknown>) => {
    const allowedKinds = ['vyr', 'vyr1', 'obch', 'serv', 'rekl'];
    // Orders status — Filip's letter codebook (XLSX 2026-06-06).
    const allowedStatuses = ['r', 'p', 'v', 'f', 'u', 'z'];
    const allowedSorts = [
      'code',
      '-code',
      'opened_at',
      '-opened_at',
      'due_date',
      '-due_date',
      'status',
      '-status',
      'total_price',
      '-total_price',
    ];
    const kind =
      typeof search.kind === 'string' && allowedKinds.includes(search.kind)
        ? (search.kind as 'vyr' | 'vyr1' | 'obch' | 'serv' | 'rekl')
        : undefined;
    const status = allowedStatuses.includes(String(search.status))
      ? (String(search.status) as 'r' | 'p' | 'v' | 'f' | 'u' | 'z')
      : undefined;
    return {
      q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
      kind,
      status,
      partner_id: typeof search.partner_id === 'string' ? search.partner_id : undefined,
      sort:
        typeof search.sort === 'string' && allowedSorts.includes(search.sort)
          ? search.sort
          : undefined,
      deadline_from:
        typeof search.deadline_from === 'string' && search.deadline_from.length > 0
          ? search.deadline_from
          : undefined,
      deadline_to:
        typeof search.deadline_to === 'string' && search.deadline_to.length > 0
          ? search.deadline_to
          : undefined,
    };
  },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker', 'partner']}>
        <OrdersPage />
      </RequireRole>,
    ),
});

const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  // `$id` is TanStack Router's dynamic segment — only `/orders` (parent) is
  // a sibling static route, so no collision risk.
  path: '/orders/$id',
  staticData: { breadcrumb: 'Detail zakázky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker', 'partner']}>
        <OrderDetailPage />
      </RequireRole>,
    ),
});

const offersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offers',
  staticData: { breadcrumb: 'Nabídky' },
  // URL-driven filters (parity with /orders) so Filip can deep-link a filtered
  // offers view and the state survives a reload. Permissive — unknown values
  // fall back to undefined (unfiltered). `status` here is the OBIS numeric
  // codebook (free string incl. legacy cohort), distinct from /orders letters.
  validateSearch: (search: Record<string, unknown>) => {
    const allowedKinds = ['nab', 'protokol', 'vyr', 'davky'];
    const allowedWorkflow = ['new', 'sent', 'negotiation', 'accepted', 'rejected'];
    const allowedSorts = [
      'code',
      '-code',
      'sent_at',
      '-sent_at',
      'issued_at',
      '-issued_at',
      'status',
      '-status',
      'total_price',
      '-total_price',
    ];
    const kind =
      typeof search.kind === 'string' && allowedKinds.includes(search.kind)
        ? (search.kind as 'nab' | 'protokol' | 'vyr' | 'davky')
        : undefined;
    const workflow_status =
      typeof search.workflow_status === 'string' && allowedWorkflow.includes(search.workflow_status)
        ? (search.workflow_status as 'new' | 'sent' | 'negotiation' | 'accepted' | 'rejected')
        : undefined;
    return {
      q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
      kind,
      // OBIS STAV is the numeric codebook (1, 2, …) plus legacy letters. The
      // router may parse `?status=2` as a number, so coerce to string before
      // checking — otherwise a deep-linked numeric status would be dropped.
      status:
        search.status !== undefined && String(search.status).length > 0
          ? String(search.status)
          : undefined,
      workflow_status,
      sort:
        typeof search.sort === 'string' && allowedSorts.includes(search.sort)
          ? search.sort
          : undefined,
    };
  },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker', 'partner']}>
        <OffersPage />
      </RequireRole>,
    ),
});

const offerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/offers/$id',
  staticData: { breadcrumb: 'Detail nabídky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker', 'partner']}>
        <OfferDetailPage />
      </RequireRole>,
    ),
});

/**
 * Sdílený validateSearch pro /partners (Adresář) i /partneri (Partneři) —
 * stejný filter model (#72030 partner_status + #72180 tags). Vytaženo, aby
 * obě routes měly identický URL kontrakt.
 */
function validatePartnersSearch(search: Record<string, unknown>): {
  partner_status: 'active' | 'inactive' | 'all';
  tags: string[];
  technologies: string[];
  sort: 'company_name' | '-company_name' | 'ico' | '-ico' | 'status' | '-status' | undefined;
} {
  const allowed = ['active', 'inactive', 'all'] as const;
  const allowedSorts = [
    'company_name',
    '-company_name',
    'ico',
    '-ico',
    'status',
    '-status',
  ] as const;
  type PartnerStatusFilter = (typeof allowed)[number];
  const raw = typeof search.partner_status === 'string' ? search.partner_status : 'active';
  const partner_status: PartnerStatusFilter = (allowed as readonly string[]).includes(raw)
    ? (raw as PartnerStatusFilter)
    : 'active';
  // tags: TanStack serializuje array do URL přes opakované parametry; toleruj
  // scalar (jeden param) i pole.
  let tags: string[] = [];
  if (Array.isArray(search.tags)) {
    tags = search.tags.filter((t): t is string => typeof t === 'string' && t.length > 0);
  } else if (typeof search.tags === 'string' && search.tags.length > 0) {
    tags = [search.tags];
  }
  let technologies: string[] = [];
  if (Array.isArray(search.technologies)) {
    technologies = search.technologies.filter(
      (t): t is string => typeof t === 'string' && t.length > 0,
    );
  } else if (typeof search.technologies === 'string' && search.technologies.length > 0) {
    technologies = [search.technologies];
  }
  const sort =
    typeof search.sort === 'string' && (allowedSorts as readonly string[]).includes(search.sort)
      ? (search.sort as (typeof allowedSorts)[number])
      : undefined;
  return { partner_status, tags, technologies, sort };
}

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners',
  staticData: { breadcrumb: 'Adresář' },
  validateSearch: validatePartnersSearch,
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      // #72193 — adresář sjednocen pod CRM (dříve admin-only „Partneři"
      // group). Filip (decision_maker) ho potřebuje stejně jako admin —
      // partner-specific admin akce (link na user / generate login) se
      // FE-side gate řeší až M5 partner self-service.
      <RequireRole allow={['admin', 'decision_maker']}>
        <PartnersPage config={ADDRESS_BOOK_CONFIG} />
      </RequireRole>,
    ),
});

// #72154 — „Partneři" = samostatná správcovská sekce. Stejná komponenta
// PartnersPage, ale s PARTNERS_CONFIG (natvrdo filtr na system:partner, bez
// role toggles). Vlastní cesta /partneri, aby se v sidebaru nepletla s
// CRM → Adresář (/partners).
const partneriRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partneri',
  staticData: { breadcrumb: 'Partneři' },
  validateSearch: validatePartnersSearch,
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <PartnersPage config={PARTNERS_CONFIG} />
      </RequireRole>,
    ),
});

const partnerDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners/$id',
  staticData: { breadcrumb: 'Detail kontaktu' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      // #72193 — viz partnersRoute komentář; decision_maker má stejný
      // přístup jako admin (kontakty, machines, edit s detach checkboxem).
      <RequireRole allow={['admin', 'decision_maker']}>
        <PartnerDetailPage />
      </RequireRole>,
    ),
});

const partnerOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partner',
  staticData: { breadcrumb: 'Přehled' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['partner']}>
        <PartnerOverviewPage />
      </RequireRole>,
    ),
});

// ─── Partner portal (#72154) — separate /portal/* shell ─────────────────────
const portalHomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalHomePage />),
});

const portalProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/profil',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalProfilePage />),
});

const portalMachinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/stroje',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalMachinesPage />),
});

const portalOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/zakazky',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalOrdersPage />),
});

const portalOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/zakazky/$id',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalOrderDetailPage />),
});

const portalCalendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/kalendar',
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () => portalShell(<PortalCalendarPage />),
});

const crmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm',
  staticData: { breadcrumb: 'CRM' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <CrmPage />
      </RequireRole>,
    ),
});

// #72505 — CRM nástěnka (pipeline + komunikace counters).
const crmDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/nastenka',
  staticData: { breadcrumb: 'Přehled' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <CrmDashboardPage />
      </RequireRole>,
    ),
});

const crmCustomersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/customers',
  // #72193 sjednocení CRM/Adresář — `/crm/customers` byl duplicitní pohled
  // nad `app.partners` (PR #91b). Filip 11.6. potvrdil že to mate; OBIS
  // má jeden adresář (`PARTNERI_PARTNERI`, 4 520 řádků, žádný kostkovací
  // flag zákazník/dodavatel). Konsolidace: `/partners` je single source of
  // truth s detail page, machines, badges + edit modal. Bookmark safety
  // přes route-level redirect (TanStack `beforeLoad` throw).
  beforeLoad: () => {
    throw redirect({
      to: '/partners',
      search: { partner_status: 'active', tags: [], technologies: [], sort: undefined },
    });
  },
  // Komponenta nikdy nebude rendrovat (beforeLoad redirect), ale TanStack
  // route definici vyžaduje. Vrátíme prázdný element pro typesafety.
  component: () => null,
});

const crmActivitiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/aktivity',
  staticData: { breadcrumb: 'Aktivity' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <CrmActivitiesPage />
      </RequireRole>,
    ),
});

// #72197 — přehled schůzek napříč partnery (kind=meeting v crm_activities).
const crmMeetingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/schuzky',
  staticData: { breadcrumb: 'Schůzky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <MeetingsPage />
      </RequireRole>,
    ),
});

// #72196 — kanban board poptávek napříč partnery (app.crm_deals).
const crmPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/crm/pipeline',
  staticData: { breadcrumb: 'Poptávky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <PipelinePage />
      </RequireRole>,
    ),
});

const manufacturingRfqRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vyroba/poptavky',
  staticData: { breadcrumb: 'Poptávky k partnerům' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ManufacturingRfqPage />
      </RequireRole>,
    ),
});

// Standalone detail poptávky — `id` samo o sobě není unikátní napříč zdroji
// (app subcontracts vs OBIS supplier_orders jsou dva oddělené UUID prostory),
// proto je `source` součástí cesty. Klik na řádek v listu i dál otevírá rychlý
// modal-náhled; ten teď nese „Otevřít detail" odkaz sem.
const manufacturingRfqDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vyroba/poptavky/$source/$id',
  staticData: { breadcrumb: 'Detail poptávky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <RfqDetailPage />
      </RequireRole>,
    ),
});

// #72505 — Výroba přehled (zakázky + subdodávky counters + Gantt časová osa).
// Gantt sekce drží okno (`from`) + úroveň zoomu ve search téhle stránky, takže
// pohled je sdílitelný a back-button safe (dřív samostatná /vyroba/gantt).
const manufacturingDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vyroba/nastenka',
  staticData: { breadcrumb: 'Přehled' },
  validateSearch: (search: Record<string, unknown>) => {
    const zooms = ['month', 'quarter', 'year'];
    return {
      from: typeof search.from === 'string' && search.from.length > 0 ? search.from : undefined,
      zoom:
        typeof search.zoom === 'string' && zooms.includes(search.zoom)
          ? (search.zoom as 'month' | 'quarter' | 'year')
          : undefined,
    };
  },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ManufacturingDashboardPage />
      </RequireRole>,
    ),
});

// #gantt — Gantt se sloučil do Výroba → Přehled. Starou URL držíme jako redirect
// (bookmark safety), přenášíme případné `from`/`zoom` z původního odkazu.
const ganttRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vyroba/gantt',
  validateSearch: (search: Record<string, unknown>) => {
    const zooms = ['month', 'quarter', 'year'];
    return {
      from: typeof search.from === 'string' && search.from.length > 0 ? search.from : undefined,
      zoom:
        typeof search.zoom === 'string' && zooms.includes(search.zoom)
          ? (search.zoom as 'month' | 'quarter' | 'year')
          : undefined,
    };
  },
  beforeLoad: ({ search }) => {
    throw redirect({ to: '/vyroba/nastenka', search });
  },
  component: () => null,
});

// ─── Reports (M3 — skeleton, PR #90 doplní reálný obsah) ────────────────────

const reportsOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports',
  staticData: { breadcrumb: 'Reporty' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportsOverviewPage />
      </RequireRole>,
    ),
});

const reportSalesPipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/sales-pipeline',
  staticData: { breadcrumb: 'Sales pipeline' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportSalesPipelinePage />
      </RequireRole>,
    ),
});

const reportCustomersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/customers',
  staticData: { breadcrumb: 'Top zákazníci' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportCustomersPage />
      </RequireRole>,
    ),
});

// #72187 — souhrnné přehledy „objednávky (partner) a zakázky (zákazník)"
const reportCustomerOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/customer-orders',
  staticData: { breadcrumb: 'Zakázky zákazníků' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportCustomerOrdersPage />
      </RequireRole>,
    ),
});

const reportPartnerOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/partner-orders',
  staticData: { breadcrumb: 'Objednávky partnerům' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportPartnerOrdersPage />
      </RequireRole>,
    ),
});

const reportOperationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/operations',
  staticData: { breadcrumb: 'Operations' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportOperationsPage />
      </RequireRole>,
    ),
});

const reportOrderFlowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reports/order-flow',
  staticData: { breadcrumb: 'Průchod zakázky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin', 'decision_maker']}>
        <ReportOrderFlowPage />
      </RequireRole>,
    ),
});

// ─── Admin: user management (existing) ──────────────────────────────────────

const usersListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  staticData: { breadcrumb: 'Uživatelé' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <UsersListPage />
      </RequireRole>,
    ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users/new',
  staticData: { breadcrumb: 'Nový uživatel' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <UserCreatePage />
      </RequireRole>,
    ),
});

const userEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users/$id',
  // Dynamic crumb — once we wire a route loader for the user we can show
  // their email here; for now a stable label keeps the chain readable.
  staticData: { breadcrumb: 'Detail uživatele' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <UserEditPage />
      </RequireRole>,
    ),
});

// ─── Admin: roles & permissions (ADR-0026) ──────────────────────────────────

const rolesListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles',
  staticData: { breadcrumb: 'Role a oprávnění' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <RolesListPage />
      </RequireRole>,
    ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles/new',
  staticData: { breadcrumb: 'Nová role' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <RoleCreatePage />
      </RequireRole>,
    ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/roles/$id',
  staticData: { breadcrumb: 'Detail role' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <RoleEditPage />
      </RequireRole>,
    ),
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/docs',
  staticData: { breadcrumb: 'Dokumentace' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <DocsPage />
      </RequireRole>,
    ),
});

const dataDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/docs/data',
  staticData: { breadcrumb: 'Dokumentace dat' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <DataDocsPage />
      </RequireRole>,
    ),
});

const changelogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/changelog',
  staticData: { breadcrumb: 'Changelog' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <ChangelogPage />
      </RequireRole>,
    ),
});

const syncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/sync',
  staticData: { breadcrumb: 'Synchronizace' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <SyncPage />
      </RequireRole>,
    ),
});

const outboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/outbox',
  staticData: { breadcrumb: 'Outbox' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <OutboxPage />
      </RequireRole>,
    ),
});

const cronJobsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/cron',
  staticData: { breadcrumb: 'Cron joby' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <CronJobsPage />
      </RequireRole>,
    ),
});

const logsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/logs',
  staticData: { breadcrumb: 'Logy' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <LogsPage />
      </RequireRole>,
    ),
});

const codebooksRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/codebooks',
  staticData: { breadcrumb: 'Číselníky' },
  beforeLoad: ({ context, location }) => authGuard(context, location.pathname),
  component: () =>
    protectedShell(
      <RequireRole allow={['admin']}>
        <CodebooksPage />
      </RequireRole>,
    ),
});

// ─── Public routes (no AppShell, no auth guard) ─────────────────────────────

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: function AcceptInviteRoute() {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AcceptInvitePage />
      </Suspense>
    );
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: function ForgotPasswordRoute() {
    return (
      <Suspense fallback={<RouteFallback />}>
        <ForgotPasswordPage />
      </Suspense>
    );
  },
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: function ResetPasswordRoute() {
    return (
      <Suspense fallback={<RouteFallback />}>
        <ResetPasswordPage />
      </Suspense>
    );
  },
});

function RouteFallback() {
  return (
    <div className="flex min-h-full items-center justify-center p-8 text-sm text-slate-500">
      Načítání…
    </div>
  );
}

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  dashboardRoute,
  changePasswordRoute,
  ordersRoute,
  orderDetailRoute,
  offersRoute,
  offerDetailRoute,
  partnerOverviewRoute,
  portalHomeRoute,
  portalProfileRoute,
  portalMachinesRoute,
  portalOrdersRoute,
  portalOrderDetailRoute,
  portalCalendarRoute,
  partnersRoute,
  partneriRoute,
  partnerDetailRoute,
  crmRoute,
  crmCustomersRoute,
  crmActivitiesRoute,
  crmMeetingsRoute,
  crmPipelineRoute,
  crmDashboardRoute,
  manufacturingRfqRoute,
  manufacturingRfqDetailRoute,
  manufacturingDashboardRoute,
  ganttRoute,
  // Reports — /reports root before children (static segments before dynamics anyway).
  reportsOverviewRoute,
  reportSalesPipelineRoute,
  reportCustomersRoute,
  reportCustomerOrdersRoute,
  reportPartnerOrdersRoute,
  reportOperationsRoute,
  reportOrderFlowRoute,
  // /admin/users/new before /admin/users/$id so static path wins over the dynamic one.
  usersListRoute,
  userCreateRoute,
  userEditRoute,
  // /admin/roles/new before /admin/roles/$id — same static-before-dynamic rule.
  rolesListRoute,
  roleCreateRoute,
  roleEditRoute,
  // /admin/docs/data before /admin/docs catch-all (both static — order doesn't matter,
  // but listed together for readability).
  docsRoute,
  dataDocsRoute,
  changelogRoute,
  // Systém pages
  syncRoute,
  outboxRoute,
  cronJobsRoute,
  logsRoute,
  codebooksRoute,
  // Public invitation / password reset routes.
  acceptInviteRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
]);
